import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSpinner, faUser } from '@fortawesome/free-solid-svg-icons';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { usersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const UserProfile = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileData, setProfileData] = useState(null);
  
  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await usersAPI.getProfile();
      
      if (response.data.status === 200) {
        setProfileData(response.data.data.user);
      } else {
        setError('Failed to load profile data');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Name is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    current_password: Yup.string()
      .when('new_password', {
        is: val => val && val.length > 0,
        then: () => Yup.string().required('Current password is required to change password'),
        otherwise: () => Yup.string()
      }),
    new_password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .nullable(),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('new_password'), null], 'Passwords must match')
      .when('new_password', {
        is: val => val && val.length > 0,
        then: () => Yup.string().required('Confirm password is required'),
        otherwise: () => Yup.string()
      })
  });

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setError('');
    setSuccess('');
    
    try {
      // Prepare data for API call
      const updateData = {
        name: values.name,
        email: values.email
      };
      
      // Only include password fields if user is updating password
      if (values.new_password) {
        updateData.current_password = values.current_password;
        updateData.new_password = values.new_password;
      }
      
      const response = await usersAPI.updateProfile(updateData);
      
      if (response.data.status === 200) {
        setSuccess('Profile updated successfully');
        
        // If email was changed, show appropriate message
        if (values.email !== profileData.email) {
          setSuccess('Profile updated successfully. If you changed your email, please verify the new email address.');
        }
        
        // Refresh profile data
        fetchUserProfile();
      } else {
        setError(response.data.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      
      if (err.response) {
        setError(err.response.data.message || 'Failed to update profile');
      } else {
        setError('Network error. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !profileData) {
    return (
      <Container className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
        <h3>Loading profile...</h3>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-3">My Profile</h1>
        </Col>
      </Row>
      
      <Row>
        <Col lg={8}>
          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Profile Information</h5>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              
              {profileData && (
                <Formik
                  initialValues={{
                    name: profileData.name || '',
                    email: profileData.email || '',
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ 
                    handleSubmit, 
                    handleChange, 
                    handleBlur, 
                    values, 
                    touched, 
                    errors, 
                    isSubmitting 
                  }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.name && !!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.email && !!errors.email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                          If you change your email, you'll need to verify the new address.
                        </Form.Text>
                      </Form.Group>
                      
                      <hr className="my-4" />
                      
                      <h5>Change Password</h5>
                      <p className="text-muted mb-3">Leave blank if you don't want to change your password</p>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Current Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="current_password"
                          value={values.current_password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.current_password && !!errors.current_password}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.current_password}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>New Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="new_password"
                          value={values.new_password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.new_password && !!errors.new_password}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.new_password}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Confirm New Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="confirm_password"
                          value={values.confirm_password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.confirm_password && !!errors.confirm_password}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.confirm_password}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <div className="d-flex justify-content-end">
                        <Button 
                          type="submit" 
                          variant="primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSave} className="me-2" />
                              Save Changes
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card className="shadow-sm mb-4">
            <Card.Header>
              <h5 className="mb-0">Account Information</h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-3">
                <div className="avatar-circle mx-auto mb-3">
                  <FontAwesomeIcon icon={faUser} size="3x" className="text-primary" />
                </div>
                <h4>{profileData?.name || 'User'}</h4>
                <p className="text-muted">{profileData?.email || ''}</p>
              </div>
              
              <hr />
              
              <p><strong>Account Created:</strong> {profileData ? new Date(profileData.created_at).toLocaleDateString() : '-'}</p>
              <p><strong>Email Verified:</strong> {profileData?.email_verified ? 'Yes' : 'No'}</p>
              <p><strong>Projects:</strong> {profileData?.project_count || 0}</p>
              <p><strong>Documents:</strong> {profileData?.document_count || 0}</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserProfile;
